import {
    createContext,
    useState,
    useEffect,
    useContext,
    useCallback,
} from "react";
import GraphicalViewerImageService from "../../services/GraphicalViewerImageService";
import { AdminAuctionContext } from "./AdminAuctionContext";

const initalState = {
    files: [],
    colorizedFiles: [],
    currentMapFile: null,
    graphicalViewerMapShowing: false,
    currentColorizedFile: null,
};

export const GraphicalViewerMapContext = createContext(initalState);

export default function GraphicalViewerMapProvider({ children }) {
    const [graphicalViewerMapContext, setGraphicalViewerMapContext] =
        useState(initalState);
    const { currentAuctionId } = useContext(AdminAuctionContext);

    const getCurrentAuctionImages = useCallback(async () => {
        const graphicalViewerImages =
            await GraphicalViewerImageService.GetGraphicalViewerImages(
                currentAuctionId
            );
        graphicalViewerImages.data.forEach(async (img) => {
            setGraphicalViewerMapContext((prev) => {
                const newFile = {
                    name: img.name,
                    preview: `${process.env.REACT_APP_BASE_URL}/api/gvimage/${img.name}`,
                    id: img.id.toString(),
                    order: img.order,
                    graphicalViewerData: img.data ? JSON.parse(img.data) : null,
                };

                return { ...prev, files: [...prev.files, newFile] };
            });
            if (img.colorized_name) {
                setGraphicalViewerMapContext((prev) => {
                    const newFile = {
                        name: img.colorized_name,
                        preview: `${process.env.REACT_APP_BASE_URL}/api/gvimage/${img.colorized_name}`,
                        id: img.id.toString(),
                        graphicalViewerData: img.data
                            ? JSON.parse(img.data)
                            : null,
                    };

                    return {
                        ...prev,
                        colorizedFiles: [...prev.colorizedFiles, newFile],
                    };
                });
            }
        });

        setGraphicalViewerMapContext((prev) => {
            return {
                ...prev,
                files: prev.files.sort((a, b) => a.order - b.order),
            };
        });
    }, [currentAuctionId]);

    useEffect(() => {
        if (graphicalViewerMapContext.files.length === 0)
            getCurrentAuctionImages();
    }, [graphicalViewerMapContext.files.length, getCurrentAuctionImages]);

    useEffect(() => {
        // Make sure to revoke the data uris to avoid memory leaks
        graphicalViewerMapContext.files.forEach((file) =>
            URL.revokeObjectURL(file.preview)
        );
        graphicalViewerMapContext.colorizedFiles.forEach((file) =>
            URL.revokeObjectURL(file.preview)
        );
    }, [
        graphicalViewerMapContext.files,
        graphicalViewerMapContext.colorizedFiles,
    ]);

    const deleteGraphicalViewerImage = async (id, preview) => {
        try {
            await GraphicalViewerImageService.DeleteGraphicalViewerImage(id);
            setGraphicalViewerMapContext((prev) => {
                return {
                    ...prev,
                    files: prev.files.filter((file) => file.id !== id),
                };
            });
        } catch (error) {
            console.log(error);
        }
    };

    const uploadGraphicalViewerImage = async (file) => {
        try {
            const data = new FormData();
            data.append("image", file);
            data.append("auction_id", currentAuctionId);
            const imageUpload =
                await GraphicalViewerImageService.UploadGraphicalViewerImage(
                    data
                );
            setGraphicalViewerMapContext((prev) => {
                const newFile = Object.assign(file, {
                    preview: `${process.env.REACT_APP_BASE_URL}/api/gvimage/${imageUpload.name}`,
                    id: imageUpload.id.toString(),
                });

                return { ...prev, files: [...prev.files, newFile] };
            });
        } catch (error) {
            console.log(error);
        }
    };

    const reorderImages = (files) => {
        files.forEach((img, i) => {
            if (parseInt(img.order) !== i + 1)
                GraphicalViewerImageService.UploadGraphicalViewerImage(img.id, {
                    graphical_viewer_image_order: i + 1,
                });
        });
    };

    return (
        <GraphicalViewerMapContext.Provider
            value={{
                ...graphicalViewerMapContext,
                setGraphicalViewerMapContext,
                uploadGraphicalViewerImage,
                deleteGraphicalViewerImage,
                reorderImages,
            }}
        >
            {children}
        </GraphicalViewerMapContext.Provider>
    );
}
